// 관리자 — 전국 치과 목록 (2026-09-06 대표 지시)
// 심평원(HIRA) 병원정보를 담은 hospitals 컬렉션(19,639건)을 그대로 읽는다. 새 컬렉션 없음.
// 19,639건을 한 번에 내리지 않는다: 시도(必)·시군구(선택)는 서버 쿼리(where + orderBy name + limit 500 + 더 보기),
// 이름·전화·주소 검색/개원일/가입·통화 체크는 불러온 행에 대해 클라이언트 필터.
// 관리자가 쓰는 필드는 통화 기록(lastCallAt·callCount·callCountByMonth — 환자앱 trackPatientCall과 같은 규약)과 adminMemo뿐(rules hasOnly).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    collection, getDocs, doc, updateDoc, query, where, orderBy, limit, startAfter,
    serverTimestamp, increment, getCountFromServer,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as XLSX from 'xlsx';
import { db } from '../../firebase/config';
import { Hospital, RefreshCw, Phone, MapPin, Download, PhoneCall, ChevronDown } from 'lucide-react';

const PAGE = 500;
const MAX_AUTO_PAGES = 20;   // [끝까지 불러오기] 상한 (500×20 = 1만 건 — 시도 하나가 넘을 일은 없음)
// 심평원 sidoCdNm 표기 후보. 실제 저장값이 어느 쪽인지 코드에서 단정하지 않고, 건수(count)가 0보다 큰 이름만 셀렉트에 올린다.
const SIDO_CANDIDATES = [
    '서울', '서울특별시', '부산', '부산광역시', '대구', '대구광역시', '인천', '인천광역시', '광주', '광주광역시',
    '대전', '대전광역시', '울산', '울산광역시', '세종', '세종특별자치시', '경기', '경기도', '강원', '강원도', '강원특별자치도',
    '충북', '충청북도', '충남', '충청남도', '전북', '전라북도', '전북특별자치도', '전남', '전라남도',
    '경북', '경상북도', '경남', '경상남도', '제주', '제주도', '제주특별자치도',
];

const toDate = (t) => t ? (typeof t.toDate === 'function' ? t.toDate() : new Date(t)) : null;
const pad = (n) => String(n).padStart(2, '0');
const fmtTs = (t) => { const d = toDate(t); return d ? `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}` : '-'; };
// establishDate는 심평원 원문 그대로 'YYYYMMDD' 문자열
const fmtEst = (s) => (s && /^\d{8}$/.test(String(s))) ? `${String(s).slice(0, 4)}.${String(s).slice(4, 6)}.${String(s).slice(6, 8)}` : (s || '-');
const inputToYmd = (v) => v ? v.replace(/-/g, '') : '';
const todayYmd = () => { const d = new Date(); return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`; };
const hasCall = (r) => (r.callCount || 0) > 0 || !!r.lastCallAt;

function AdminClinicDirectory() {
    const [sidoOptions, setSidoOptions] = useState([]);   // [{ name, count }]
    const [sidoLoading, setSidoLoading] = useState(true);
    const [lastSynced, setLastSynced] = useState(null);
    const [sido, setSido] = useState('');
    const [sggu, setSggu] = useState('');
    const [text, setText] = useState('');
    const [estFrom, setEstFrom] = useState('');
    const [estTo, setEstTo] = useState('');
    const [onlyClaimed, setOnlyClaimed] = useState(false);
    const [onlyCalled, setOnlyCalled] = useState(false);
    const [rows, setRows] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');
    const [syncAvailable, setSyncAvailable] = useState(true);   // callable adminSyncHospitals 미배포(not-found)면 버튼 숨김
    const [memoDraft, setMemoDraft] = useState({});
    const [busyId, setBusyId] = useState('');
    const lastDocRef = useRef(null);
    const reqSeq = useRef(0);   // 시도/시군구 빠른 전환 시 늦게 온 응답 무시

    // 시도 목록: 후보 이름별 count 집계(문서 다운로드 없음) → 있는 것만. 마지막 갱신은 lastSyncedAt 최댓값 1건.
    useEffect(() => {
        let alive = true;
        (async () => {
            setSidoLoading(true);
            try {
                const col = collection(db, 'hospitals');
                const [counts, lastSnap] = await Promise.all([
                    Promise.all(SIDO_CANDIDATES.map(async (name) => {
                        const c = await getCountFromServer(query(col, where('sido', '==', name)));
                        return { name, count: c.data().count };
                    })),
                    getDocs(query(col, orderBy('lastSyncedAt', 'desc'), limit(1))),
                ]);
                if (!alive) return;
                setSidoOptions(counts.filter(c => c.count > 0));
                setLastSynced(lastSnap.empty ? null : lastSnap.docs[0].data().lastSyncedAt);
            } catch (e) {
                console.error('[전국치과] 시도 목록 조회 실패:', e);
                if (alive) setLoadError('시도 목록을 불러오지 못했습니다: ' + (e?.message || e));
            } finally { if (alive) setSidoLoading(false); }
        })();
        return () => { alive = false; };
    }, []);

    const buildQuery = useCallback((after) => {
        const parts = [collection(db, 'hospitals'), where('sido', '==', sido)];
        if (sggu) parts.push(where('sggu', '==', sggu));
        parts.push(orderBy('name', 'asc'));
        if (after) parts.push(startAfter(after));
        parts.push(limit(PAGE));
        return query(...parts);
    }, [sido, sggu]);

    // 첫 페이지 (시도/시군구 변경 시)
    useEffect(() => {
        if (!sido) { setRows([]); setHasMore(false); lastDocRef.current = null; return; }
        const seq = ++reqSeq.current;
        let alive = true;
        (async () => {
            setLoading(true); setLoadError('');
            try {
                const snap = await getDocs(buildQuery(null));
                if (!alive || seq !== reqSeq.current) return;
                setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
                setHasMore(snap.size === PAGE);
            } catch (e) {
                console.error('[전국치과] 목록 조회 실패:', e);
                if (alive) setLoadError('목록을 불러오지 못했습니다: ' + (e?.message || e));
            } finally { if (alive && seq === reqSeq.current) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [sido, sggu, buildQuery]);

    const loadMore = useCallback(async (all = false) => {
        if (!sido || !lastDocRef.current || loading) return;
        const seq = reqSeq.current;
        setLoading(true); setLoadError('');
        try {
            let pages = 0;
            let more = true;
            while (more && pages < (all ? MAX_AUTO_PAGES : 1)) {
                const snap = await getDocs(buildQuery(lastDocRef.current));
                if (seq !== reqSeq.current) return;
                const add = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setRows(prev => prev.concat(add));
                lastDocRef.current = snap.docs[snap.docs.length - 1] || lastDocRef.current;
                more = snap.size === PAGE;
                pages += 1;
            }
            setHasMore(more);
        } catch (e) {
            console.error('[전국치과] 더 보기 실패:', e);
            setLoadError('더 불러오지 못했습니다: ' + (e?.message || e));
        } finally { if (seq === reqSeq.current) setLoading(false); }
    }, [sido, loading, buildQuery]);

    // 시군구 옵션은 불러온 행에서 추출(시도 전체를 내리지 않으므로 [끝까지 불러오기] 전엔 일부일 수 있음)
    const sgguOptions = useMemo(() => {
        const s = new Set(rows.map(r => r.sggu).filter(Boolean));
        if (sggu) s.add(sggu);
        return Array.from(s).sort((a, b) => a.localeCompare(b, 'ko'));
    }, [rows, sggu]);

    const filtered = useMemo(() => {
        const q = text.trim().toLowerCase();
        const qDigits = q.replace(/\D/g, '');
        const from = inputToYmd(estFrom), to = inputToYmd(estTo);
        return rows.filter(r => {
            if (q) {
                const hitText = `${r.name || ''} ${r.address || ''}`.toLowerCase().includes(q);
                const hitPhone = qDigits && String(r.phone || '').replace(/\D/g, '').includes(qDigits);
                if (!hitText && !hitPhone) return false;
            }
            if (from || to) {
                const e = String(r.establishDate || '');
                if (!/^\d{8}$/.test(e)) return false;
                if (from && e < from) return false;
                if (to && e > to) return false;
            }
            if (onlyClaimed && !r.claimedBy) return false;
            if (onlyCalled && !hasCall(r)) return false;
            return true;
        });
    }, [rows, text, estFrom, estTo, onlyClaimed, onlyCalled]);

    // [통화 기록 +1] — 환자앱 trackPatientCall(functions/index.js)과 같은 필드 규약
    const logCall = async (row) => {
        if (busyId) return;
        setBusyId(row.id);
        const ym = new Date().toISOString().substring(0, 7);
        try {
            await updateDoc(doc(db, 'hospitals', row.id), {
                callCount: increment(1),
                lastCallAt: serverTimestamp(),
                [`callCountByMonth.${ym}`]: increment(1),
            });
            setRows(prev => prev.map(r => r.id === row.id ? {
                ...r, callCount: (r.callCount || 0) + 1, lastCallAt: new Date(),
                callCountByMonth: { ...(r.callCountByMonth || {}), [ym]: ((r.callCountByMonth || {})[ym] || 0) + 1 },
            } : r));
        } catch (e) {
            console.error('[전국치과] 통화 기록 실패:', e);
            alert('통화 기록 저장 실패: ' + (e?.message || e));
        } finally { setBusyId(''); }
    };

    const saveMemo = async (row) => {
        const memo = (memoDraft[row.id] ?? row.adminMemo ?? '').trim();
        try {
            await updateDoc(doc(db, 'hospitals', row.id), { adminMemo: memo, adminMemoAt: serverTimestamp() });
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, adminMemo: memo } : r));
            setMemoDraft(prev => { const n = { ...prev }; delete n[row.id]; return n; });
        } catch (e) {
            console.error('[전국치과] 메모 저장 실패:', e);
            alert('메모 저장 실패: ' + (e?.message || e));
        }
    };

    // [지금 갱신] — dev-server가 만드는 callable adminSyncHospitals. 아직 배포 전(not-found)이면 버튼을 숨긴다.
    const runSync = async () => {
        if (syncing) return;
        setSyncing(true); setSyncMsg('심평원에서 전국 치과 목록을 갱신하는 중… (수 분)');
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'adminSyncHospitals', { timeout: 540000 });
            const r = await fn({});
            const d = r.data || {};
            setSyncMsg(`완료${d.total != null ? ` — ${d.total}곳` : ''}${d.updated != null ? ` · 갱신 ${d.updated}` : ''}${d.created != null ? ` · 신규 ${d.created}` : ''}${d.errors?.length ? ` · 오류 ${d.errors.length}건` : ''}`);
            const lastSnap = await getDocs(query(collection(db, 'hospitals'), orderBy('lastSyncedAt', 'desc'), limit(1)));
            if (!lastSnap.empty) setLastSynced(lastSnap.docs[0].data().lastSyncedAt);
        } catch (e) {
            console.error('[전국치과] 수동 갱신 실패:', e);
            if (e?.code === 'functions/not-found') {
                setSyncAvailable(false);
                setSyncMsg('갱신 기능(adminSyncHospitals)이 아직 서버에 없습니다. 서버 배포 후 버튼이 다시 보입니다.');
            } else {
                setSyncMsg('실패: ' + (e?.message || e));
            }
        } finally { setSyncing(false); }
    };

    const exportExcel = () => {
        if (filtered.length === 0) { alert('내려받을 치과가 없습니다.'); return; }
        const header = ['치과명', '종별', '개원일', '전화', '주소', '시도', '시군구', '의사 수', '덴클 가입', '마지막 통화', '통화 수', '메모', '홈페이지', '심평원코드', '운영'];
        const body = filtered.map(r => [
            r.name || '', r.classification || '', fmtEst(r.establishDate), r.phone || '', r.address || '', r.sido || '', r.sggu || '',
            r.doctorCount ?? '', r.claimedBy ? '가입' : '', r.lastCallAt ? fmtTs(r.lastCallAt) : '', r.callCount || 0,
            r.adminMemo || '', r.homepage || '', r.hiraCode || r.id, r.isActive === false ? '비활성' : '',
        ]);
        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        ws['!cols'] = [22, 8, 11, 14, 44, 8, 10, 7, 8, 16, 7, 24, 24, 16, 7].map(w => ({ wch: w }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '전국치과');
        XLSX.writeFile(wb, `덴클_전국치과_${sido || '전체'}_${todayYmd()}.xlsx`);
    };

    const claimedCount = rows.filter(r => r.claimedBy).length;
    const calledCount = rows.filter(hasCall).length;
    const sidoTotal = sidoOptions.reduce((s, o) => s + o.count, 0);

    return (
        <div style={S.page}>
            <div style={S.head}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Hospital size={22} color="#6366f1" />
                    <h1 style={S.h1}>전국 치과 목록</h1>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={exportExcel} disabled={filtered.length === 0} style={{ ...S.btnO, opacity: filtered.length === 0 ? 0.5 : 1 }}>
                        <Download size={14} /> 엑셀 내려받기 ({filtered.length})
                    </button>
                    {syncAvailable && (
                        <button onClick={runSync} disabled={syncing} style={{ ...S.btnP, opacity: syncing ? 0.6 : 1 }}>
                            <RefreshCw size={14} /> {syncing ? '갱신 중…' : '지금 갱신'}
                        </button>
                    )}
                </div>
            </div>
            <div style={S.sub}>
                자료 출처 심평원(건강보험심사평가원) 병원정보 · 마지막 갱신 {fmtTs(lastSynced)}
                {sidoTotal > 0 && <span style={{ marginLeft: 8, color: '#94a3b8' }}>· 전체 {sidoTotal.toLocaleString()}곳</span>}
                <br />시도를 고르면 그 지역만 500곳씩 불러옵니다. 이름·전화·주소 검색과 개원일·가입·통화 조건은 불러온 목록 안에서 걸러집니다.
            </div>
            {syncMsg && <div style={S.notice}>{syncMsg}</div>}
            {loadError && <div style={{ ...S.notice, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{loadError}</div>}

            <div style={S.toolbar}>
                <select value={sido} onChange={e => { setSido(e.target.value); setSggu(''); }} style={S.select} disabled={sidoLoading}>
                    <option value="">{sidoLoading ? '시도 불러오는 중…' : '시도 선택'}</option>
                    {sidoOptions.map(o => <option key={o.name} value={o.name}>{o.name} ({o.count.toLocaleString()})</option>)}
                </select>
                <select value={sggu} onChange={e => setSggu(e.target.value)} style={S.select} disabled={!sido}>
                    <option value="">시군구 전체</option>
                    {sgguOptions.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <input value={text} onChange={e => setText(e.target.value)} placeholder="치과명·전화·주소 검색" style={S.input} disabled={!sido} />
                <span style={S.label}>개원일</span>
                <input type="date" value={estFrom} onChange={e => setEstFrom(e.target.value)} style={S.date} disabled={!sido} />
                <span style={{ color: '#94a3b8' }}>~</span>
                <input type="date" value={estTo} onChange={e => setEstTo(e.target.value)} style={S.date} disabled={!sido} />
                <label style={S.check}>
                    <input type="checkbox" checked={onlyClaimed} onChange={e => setOnlyClaimed(e.target.checked)} style={{ width: 'auto' }} disabled={!sido} />
                    덴클 가입 ({claimedCount})
                </label>
                <label style={S.check}>
                    <input type="checkbox" checked={onlyCalled} onChange={e => setOnlyCalled(e.target.checked)} style={{ width: 'auto' }} disabled={!sido} />
                    통화 기록 있음 ({calledCount})
                </label>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>
                    {sido ? `불러온 ${rows.length.toLocaleString()}곳 중 ${filtered.length.toLocaleString()}곳${hasMore ? ' (더 있음)' : ''}` : ''}
                </span>
            </div>

            {!sido ? (
                <div style={S.empty}>먼저 시도를 선택해 주세요.</div>
            ) : loading && rows.length === 0 ? (
                <div style={S.empty}>불러오는 중…</div>
            ) : filtered.length === 0 ? (
                <div style={S.empty}>
                    {rows.length === 0 ? '이 지역에 등록된 치과가 없습니다.' : '조건에 맞는 치과가 없습니다. 검색어나 조건을 바꿔 보세요.'}
                    {hasMore && ' 아직 안 불러온 목록이 남아 있습니다 — 아래 [더 보기]를 눌러 주세요.'}
                </div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                <th style={S.th}>치과명</th>
                                <th style={S.th}>종별</th>
                                <th style={S.th}>개원일</th>
                                <th style={S.th}>전화</th>
                                <th style={S.th}>주소</th>
                                <th style={S.th}>의사</th>
                                <th style={S.th}>가입</th>
                                <th style={S.th}>마지막 통화</th>
                                <th style={S.th}>통화</th>
                                <th style={S.th}>메모</th>
                                <th style={S.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => {
                                const draft = memoDraft[r.id];
                                return (
                                    <tr key={r.id} style={{ opacity: r.isActive === false ? 0.55 : 1 }}>
                                        <td style={S.td}>
                                            <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.name}</div>
                                            {r.homepage && <a href={r.homepage} target="_blank" rel="noreferrer" style={S.link}>{r.homepage}</a>}
                                            {r.isActive === false && <span style={S.badgeGray}>비활성</span>}
                                        </td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.classification || '-'}</td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtEst(r.establishDate)}</td>
                                        <td style={S.td}>
                                            {r.phone ? <a href={`tel:${r.phone}`} style={S.tel}><Phone size={12} /> {r.phone}</a> : <span style={S.small}>없음</span>}
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                                                <MapPin size={12} color="#94a3b8" style={{ marginTop: 3, flexShrink: 0 }} />
                                                <div>
                                                    <div>{r.address || '-'}</div>
                                                    <div style={S.small}>{r.sido} {r.sggu}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ ...S.td, textAlign: 'center' }}>{r.doctorCount ?? '-'}</td>
                                        <td style={S.td}>{r.claimedBy ? <span style={S.badgeOn}>가입</span> : <span style={S.small}>-</span>}</td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.lastCallAt ? fmtTs(r.lastCallAt) : <span style={S.small}>-</span>}</td>
                                        <td style={{ ...S.td, textAlign: 'center', fontWeight: 700 }}>{r.callCount || 0}</td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <input value={draft ?? r.adminMemo ?? ''} onChange={e => setMemoDraft(p => ({ ...p, [r.id]: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') saveMemo(r); }}
                                                    placeholder="통화 결과 등" style={S.memo} />
                                                {draft !== undefined && draft !== (r.adminMemo || '') && (
                                                    <button onClick={() => saveMemo(r)} style={S.btnMini}>저장</button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                                            <button onClick={() => logCall(r)} disabled={busyId === r.id} style={{ ...S.btnCall, opacity: busyId === r.id ? 0.6 : 1 }}>
                                                <PhoneCall size={12} /> 통화 기록 +1
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {sido && hasMore && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                    <button onClick={() => loadMore(false)} disabled={loading} style={{ ...S.btnO, opacity: loading ? 0.6 : 1 }}>
                        <ChevronDown size={14} /> {loading ? '불러오는 중…' : `더 보기 (${PAGE}곳씩)`}
                    </button>
                    <button onClick={() => loadMore(true)} disabled={loading} style={{ ...S.btnMini, opacity: loading ? 0.6 : 1 }}>
                        이 지역 끝까지 불러오기
                    </button>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { padding: 20, maxWidth: 1400, margin: '0 auto' },
    head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
    h1: { fontSize: 20, fontWeight: 800, margin: 0, color: '#0f172a' },
    sub: { fontSize: 12.5, color: '#64748b', margin: '6px 0 14px', lineHeight: 1.6 },
    notice: { background: '#EEF2FF', border: '1px solid #C7D2FE', color: '#3730A3', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 },
    toolbar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
    label: { fontSize: 12.5, fontWeight: 700, color: '#475569' },
    select: { padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff', minWidth: 140 },
    input: { flex: 1, minWidth: 200, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' },
    date: { padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit' },
    check: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' },
    empty: { padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13.5, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12 },
    tableWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflowX: 'auto' },
    table: { width: '100%', minWidth: 1180, borderCollapse: 'collapse' },
    th: { textAlign: 'left', fontSize: 11.5, fontWeight: 700, color: '#94a3b8', padding: '9px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', whiteSpace: 'nowrap' },
    td: { padding: '10px 12px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
    small: { fontSize: 11.5, color: '#94a3b8', marginTop: 2 },
    link: { display: 'block', fontSize: 11.5, color: '#64748b', textDecoration: 'none', marginTop: 2, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    tel: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#4f46e5', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' },
    memo: { width: 170, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit' },
    badgeOn: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
    badgeGray: { display: 'inline-block', marginTop: 3, padding: '1px 7px', borderRadius: 999, background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 700 },
    btnP: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 15px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    btnO: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 15px', background: '#fff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    btnMini: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    btnCall: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 6, border: 'none', background: '#0ca30c', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
};

export default AdminClinicDirectory;
